import type { ReportDocument } from "../../services/report";
import { CableLinksPanel, cableLinksOf } from "../CableLinksPanel";
import { EarthingLinksPanel } from "../EarthingLinksPanel";
import { EARTH_FAULT_LOOP_WORDING, FaultLinkPanel } from "../FaultLinkPanel";
import { LoadLinkPanel } from "../LoadLinkPanel";
import { SldLinkPanel } from "../SldLinkPanel";

// Section 7 of the sheet, the links: the SLD pin (version, content hash, warnings), the
// load-run pin of a power quality study (run, demand kW / kVA / kvar, PF; EOS-11), the
// fault-run pin (run, bus, case, type, exact Ik''; the 05d words for an earth-fault loop
// study, whose run gave Ze) and the cable-run pins of an earth-fault loop study (one per row:
// run, figures as read, status; EOS-05d) and the per-row pins of an earthing study (the
// earth-fault runs its board conductors read, the earth-fault loop rows its circuit protective
// conductors read; EOS-08a) exactly as the study pages' panels show them - the same
// components, nothing restated. A run that stored none says so.

type ReportLinksProps = {
  document: ReportDocument;
};

export const NO_LINKS_TEXT =
  "No SLD, load-run, fault-run, cable-run or earth-fault loop row link stored with this run.";

export function ReportLinks({ document }: ReportLinksProps) {
  // A document a server before EOS-05d served carries no `cable_links`; the stored
  // references of such a run still name them, so the snapshot is the fallback.
  const cableLinks =
    document.cable_links === undefined
      ? cableLinksOf(document.references_snapshot)
      : document.cable_links;
  const earthFaultLoop = document.module.calculation_type === "EARTH_FAULT_LOOP";
  const faultLinks = document.fault_links ?? null;
  const disconnectionLinks = document.disconnection_links ?? null;
  const none =
    document.sld_link === null &&
    document.fault_link === null &&
    (document.load_link === null || document.load_link === undefined) &&
    cableLinks === null &&
    faultLinks === null &&
    disconnectionLinks === null;
  return (
    <section aria-label="Links" data-report-section="links">
      <h3>Links</h3>
      {none ? (
        <p data-report-no-links="true">{NO_LINKS_TEXT}</p>
      ) : (
        <>
          <SldLinkPanel link={document.sld_link} />
          <LoadLinkPanel link={document.load_link} />
          <FaultLinkPanel
            link={document.fault_link}
            wording={earthFaultLoop ? EARTH_FAULT_LOOP_WORDING : undefined}
          />
          <CableLinksPanel links={cableLinks} />
          <EarthingLinksPanel faultLinks={faultLinks} disconnectionLinks={disconnectionLinks} />
        </>
      )}
    </section>
  );
}

export default ReportLinks;
